import { baseURL } from "../constant";
import { mutateFiles } from "./slice/fileSlice";
import { setBulkProcessUpdate } from "./slice/ingestSlice";
import { setTaskUpdate, addDiscoveredTask } from "./slice/notificationSlice";

const MAX_RECONNECT_ATTEMPTS = 5;
const TERMINAL_TASK_STATUSES = new Set([
  "COMPLETED",
  "DISMISSED",
  "FAILED",
  "FAILURE",
  "REVOKED",
  "SUCCESS",
]);

const serverSideEventMiddleware = () => {
  const connections = {};
  const reconnectTimers = {};
  const reconnectAttempts = {};

  const actionMap = {
    bulkProcess: setBulkProcessUpdate,
    discovery: addDiscoveredTask,
    docDigit: mutateFiles,
    task: setTaskUpdate,
  };

  const startEventSource = (dispatch, url, key, events = [], category) => {
    if (connections[key]) return;

    const cleanUrl = url.startsWith("/") ? url.slice(1) : url;
    const fullUrl = `${baseURL}/${cleanUrl}/`.replace(/([^:]\/)\/+/g, "$1");

    const eventSource = new EventSource(fullUrl, { withCredentials: true });

    const handler = event => {
      try {
        const data = JSON.parse(event.data);
        const actionCreator = actionMap[category || key];
        if (actionCreator) {
          dispatch(actionCreator(data));
        }
        // Task streams deliberately close after publishing their terminal state.
        // Treat that as a clean shutdown instead of reconnecting forever.
        if (
          category === "task" &&
          TERMINAL_TASK_STATUSES.has(String(data?.status || "").toUpperCase())
        ) {
          eventSource.close();
          delete connections[key];
          reconnectAttempts[key] = 0;
          return;
        }
        // Reset reconnect counter on every successful message so transient
        // network blips don't accumulate toward the MAX_RECONNECT_ATTEMPTS cap.
        reconnectAttempts[key] = 0;
      } catch (err) {
        console.error("SSE Parse Error", err);
      }
    };

    eventSource.onmessage = handler;
    if (Array.isArray(events)) {
      events.forEach(e => eventSource.addEventListener(e, handler));
    }

    eventSource.onerror = () => {
      // Ignore close/error callbacks from a stream that has already been
      // stopped or superseded (including a clean terminal task shutdown).
      if (connections[key] !== eventSource) return;

      eventSource.close();
      delete connections[key];

      const attempts = (reconnectAttempts[key] || 0) + 1;
      reconnectAttempts[key] = attempts;

      if (attempts <= MAX_RECONNECT_ATTEMPTS) {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const delay = Math.min(1000 * Math.pow(2, attempts - 1), 30000);
        reconnectTimers[key] = setTimeout(() => {
          delete reconnectTimers[key];
          startEventSource(dispatch, url, key, events, category);
        }, delay);
      }
    };

    connections[key] = eventSource;
  };

  const stopEventSource = key => {
    if (reconnectTimers[key]) {
      clearTimeout(reconnectTimers[key]);
      delete reconnectTimers[key];
    }
    reconnectAttempts[key] = 0;

    if (connections[key]) {
      connections[key].close();
      delete connections[key];
    }
  };

  return store => next => action => {
    if (["fileSSE/start"].includes(action.type)) {
      const { url, key, events, category } = action.payload;
      startEventSource(store.dispatch, url, key, events, category);
    } else if (["fileSSE/stop"].includes(action.type)) {
      stopEventSource(action.payload.key);
    }
    return next(action);
  };
};

const sseMiddleware = serverSideEventMiddleware();
export { sseMiddleware };
